import { useMemo } from 'react';
import { DEFAULT_DISPLAY_STATUS, PRESENT_DATE_VALUE, validateEmploymentRecord } from '../constants';
import {
  BackendOrganizationRecord,
  EmploymentRecord,
  GroupedEmploymentData
} from 'utils/employment/types';
import { transformToGroupedTimeline } from 'utils/employment/transformers';

/**
 * Transform complex organizations array to simple employment list
 * Flattens the organizations[].employee.roles[] hierarchy for UI display
 */
export const transformEmploymentData = (organizations: BackendOrganizationRecord[]): EmploymentRecord[] => {
  if (!organizations || !Array.isArray(organizations)) {
    return [];
  }

  return organizations.flatMap(org => {
    if (!org?.employee?.roles || !Array.isArray(org.employee.roles)) {
      return [];
    }

    return org.employee.roles.map(role => {
      return {
        id: `${org.organization.id}-${role.id}`, // ✅ Fixed: Use org.organization.id instead of org.id
        organization: org.organization, // ✅ Spread entire organization object
        jobTitle: role.designation?.name || 'Employee', // Job title from designation or fallback
        designation: role.designation, // ✅ Use entire designation object
        department: role.department, // ✅ Use entire department object (already optional)
        startDate: role.startDate || '', // Handle null startDate
        endDate: role.endDate || PRESENT_DATE_VALUE, // ✅ Use constant for Present
        employmentType: role.employmentType, // ✅ Use entire employment type object (now optional)
        displayStatus: role.displayStatus || DEFAULT_DISPLAY_STATUS, // ✅ Fixed: Read from role level with fallback
        acknowledged: org.organization.acknowledged, // ✅ Fixed: Use org.organization.acknowledged
        employeeId: org.employee?.id, // Preserve employee ID for existing records
        // Add missing fields for current employment functionality
        isCurrentEmployment: role.endDate === null || role.endDate === PRESENT_DATE_VALUE,
        isEmailVerified: false, // Default to false for existing records
        accessEmail: org.employee?.accessEmail, // ✅ Add organization access email
      };
    }).filter(Boolean) as EmploymentRecord[]; // Remove null entries
  });
};

/**
 * Transform simple employment list back to complex API format for submission
 * Converts UI-friendly list back to organizations[].employee.roles[] hierarchy
 */
export const formatForSubmission = (employmentList: EmploymentRecord[]): BackendOrganizationRecord[] => {
  if (!employmentList || !Array.isArray(employmentList)) {
    return [];
  }

  try {
    // Group employment records by organization ID
    const groupedByOrganization = employmentList.reduce((acc, employment) => {
      const orgId = employment.organization.id;
      
      if (!acc[orgId]) {
        acc[orgId] = {
          organization: employment.organization, // ✅ Preserve complete organization object
          roles: [],
          status: 'not approved', // ✅ Default status from API structure
          employeeId: employment.employeeId || 0, // Use 0 for new employees, preserve existing employeeId
        };
      }
      
      // Add role to organization - matching actual API structure
      const roleData: any = {
        designation: employment.designation, // ✅ Complete designation object
        department: employment.department, // ✅ Complete department object (optional)
        startDate: employment.startDate, // ✅ ISO format string
        endDate: employment.endDate === PRESENT_DATE_VALUE ? null : employment.endDate, // ✅ Set null for current employment
        isPast: employment.endDate !== PRESENT_DATE_VALUE, // ✅ Use constant for Present check
        employmentType: employment.employmentType || null, // ✅ Handle optional employment type
        displayStatus: employment.displayStatus, // ✅ Fixed: Send displayStatus at role level
      };

      // For new records (ID starts with 'new-'), don't send ID - let backend generate it
      // For existing records, send the actual numeric ID
      if (employment.id.startsWith('new-')) {
        // New record - don't include ID field, backend will generate it
      } else {
        // Existing record - extract numeric ID
        const numericId = parseInt(employment.id.split('-')[1]) || parseInt(employment.id);
        if (!isNaN(numericId)) {
          roleData.id = numericId;
        }
      }

      acc[orgId].roles.push(roleData);
      
      return acc;
    }, {} as Record<number, any>);

    // Convert grouped data to actual API format
    const result = Object.values(groupedByOrganization).map((group: any) => {
      // Build employee object - omit ID for new employees
      const employeeData: any = {
        roles: group.roles, // ✅ Include all roles (new ones without ID, existing ones with ID)
        isPast: group.roles.every((role: any) => role.isPast), // ✅ Added from API structure
      };

      // Only include employee ID for existing employees (not new ones)
      if (group.employeeId && group.employeeId !== 0) {
        employeeData.id = group.employeeId;
      }
      // For new employees: omit id field entirely (backend will generate it)

      const organizationRecord: BackendOrganizationRecord = {
        organization: group.organization, // ✅ Complete organization object with images, etc.
        employee: employeeData,
        status: group.status, // ✅ Added from API structure
        // Note: displayStatus and acknowledged are now inside organization object
      };
      
      return organizationRecord;
    });
    
    return result;
  } catch (error) {
    console.error('Error formatting employment data for submission:', error);
    return [];
  }
};

/**
 * Custom hook for employment data management
 * Provides data transformation, validation, and utility functions
 * Extended with timeline grouping functionality
 */
export const useEmploymentData = (organizations: BackendOrganizationRecord[] = []) => {
  // Transform organizations to employment list with memoization
  const employmentList = useMemo(() => {
    try {
      return transformEmploymentData(organizations);
    } catch (error) {
      console.error('Error transforming employment data:', error);
      return [];
    }
  }, [organizations]);

  // Transform employment list to grouped timeline data
  const groupedTimelineData = useMemo(() => {
    try {
      return transformToGroupedTimeline(employmentList);
    } catch (error) {
      console.error('Error creating grouped timeline data:', error);
      // Return empty grouped data on error
      return {
        groups: [],
        totalRecords: 0,
        totalOrganizations: 0,
        isEmpty: true,
      } as GroupedEmploymentData;
    }
  }, [employmentList]);

  // Memoized submission formatter
  const formatForAPI = useMemo(() => {
    return (list: EmploymentRecord[]) => {
      try {
        return formatForSubmission(list);
      } catch (error) {
        console.error('Error formatting employment data for API:', error);
        return [];
      }
    };
  }, []);

  // Utility functions with enhanced validation
  const addEmploymentRecord = useMemo(() => (
    currentList: EmploymentRecord[],
    newRecord: Partial<EmploymentRecord>,
  ): EmploymentRecord[] => {
    // ✅ Use centralized validation
    const validation = validateEmploymentRecord(newRecord);
    if (!validation.isValid) {
      throw new Error(`Invalid employment record data: ${validation.errors.join(', ')}`);
    }

    const fullRecord: EmploymentRecord = {
      id: `new-${Date.now()}`, // Temporary ID for new records
      displayStatus: DEFAULT_DISPLAY_STATUS, // ✅ Use constant for default
      acknowledged: false, // Default to not acknowledged
      ...newRecord as EmploymentRecord,
    };

    return [...currentList, fullRecord];
  }, []);

  const updateEmploymentRecord = useMemo(() => (
    currentList: EmploymentRecord[],
    recordId: string,
    updates: Partial<EmploymentRecord>,
  ): EmploymentRecord[] => {
    return currentList.map(record =>
      record.id === recordId
        ? { ...record, ...updates }
        : record
    );
  }, []);

  const removeEmploymentRecord = useMemo(() => (
    currentList: EmploymentRecord[],
    recordId: string,
  ): EmploymentRecord[] => {
    return currentList.filter(record => record.id !== recordId);
  }, []);

  // Computed properties with memoization
  const computedProperties = useMemo(() => ({
    totalEmploymentRecords: employmentList.length,
    visibleRecords: employmentList.filter(record => record.displayStatus === DEFAULT_DISPLAY_STATUS),
    hiddenRecords: employmentList.filter(record => record.displayStatus === 1),
    acknowledgedRecords: employmentList.filter(record => record.acknowledged),
    pendingRecords: employmentList.filter(record => !record.acknowledged),
    totalOrganizationGroups: groupedTimelineData.groups.length,
    hasOngoingEmployment: groupedTimelineData.groups.some(group => group.period.end === PRESENT_DATE_VALUE),
    isEmpty: groupedTimelineData.isEmpty,
  }), [employmentList, groupedTimelineData]);

  return {
    // Original flat data (backward compatibility)
    employmentList,
    
    // New grouped timeline data
    groupedTimelineData,
    
    // Transformation utilities
    transformFromBackend: transformEmploymentData,
    formatForAPI,
    
    // Validation (now using centralized validation)
    validateRecord: validateEmploymentRecord,
    
    // CRUD utilities
    addEmploymentRecord,
    updateEmploymentRecord,
    removeEmploymentRecord,
    
    // Computed properties
    ...computedProperties,
  };
}; 